import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Admin Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import EventsPage from "@/pages/EventsPage";
import TicketsPage from "@/pages/TicketsPage";
import PrePrintedPage from "@/pages/PrePrintedPage";
import ScannerPage from "@/pages/ScannerPage";
import ReportsPage from "@/pages/ReportsPage";
import PaymentsPage from "@/pages/PaymentsPage";
import StaffManagementPage from "@/pages/StaffManagementPage";
import AdminValidatePage from "@/pages/AdminValidatePage";
import TournamentsAdminPage from "@/pages/TournamentsAdminPage";
import FixturesAdminPage from "@/pages/FixturesAdminPage";

// Public Pages
import LandingPage from "@/pages/LandingPage";
import HomePage from "@/pages/HomePage";
import EventDetailPage from "@/pages/EventDetailPage";
import UserLoginPage from "@/pages/UserLoginPage";
import UserRegisterPage from "@/pages/UserRegisterPage";
import MyTicketsPage from "@/pages/MyTicketsPage";
import PublicTicketPage from "@/pages/PublicTicketPage";
import PaymentResultPage from "@/pages/PaymentResultPage";

// Category Pages
import FootballPage from "@/pages/FootballPage";
import TournamentPage from "@/pages/TournamentPage";
import FixtureDetailPage from "@/pages/FixtureDetailPage";
import ConcertsPage from "@/pages/ConcertsPage";
import FestivalsPage from "@/pages/FestivalsPage";

// Staff Pages
import StaffLoginPage from "@/pages/StaffLoginPage";
import StaffScannerPage from "@/pages/StaffScannerPage";

// Unified Login
import UnifiedLoginPage from "@/pages/UnifiedLoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// Legal Pages
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";

const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const UserRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/user-login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Landing Page - Events First (Public) */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Public Routes */}
          <Route path="/events" element={<HomePage />} />
          <Route path="/event/:eventId" element={<EventDetailPage />} />
          <Route path="/ticket/:ticketId" element={<PublicTicketPage />} />
          <Route path="/payment-result" element={<PaymentResultPage />} />
          <Route path="/scanner" element={<ScannerPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          
          {/* Category Pages */}
          <Route path="/football" element={<FootballPage />} />
          <Route path="/football/:tournamentId" element={<TournamentPage />} />
          <Route path="/fixture/:fixtureId" element={<FixtureDetailPage />} />
          <Route path="/concerts" element={<ConcertsPage />} />
          <Route path="/festivals" element={<FestivalsPage />} />
          
          {/* User Auth Routes */}
          <Route path="/login" element={<UnifiedLoginPage />} />
          <Route path="/user-login" element={<UserLoginPage />} />
          <Route path="/user-register" element={<UserRegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/my-tickets" element={<UserRoute><MyTicketsPage /></UserRoute>} />
          
          {/* Unified Login (alternate routes) */}
          <Route path="/auth/login" element={<UnifiedLoginPage />} />
          <Route path="/signin" element={<UnifiedLoginPage />} />
          
          {/* Staff Routes */}
          <Route path="/staff/login" element={<StaffLoginPage />} />
          <Route path="/staff/scanner" element={<StaffScannerPage />} />
          
          {/* Admin Auth Routes (legacy - redirects to unified) */}
          <Route path="/admin/login" element={<LoginPage />} />
          
          {/* Admin Protected Routes */}
          <Route path="/dashboard" element={<AdminRoute><DashboardPage /></AdminRoute>} />
          <Route path="/admin/events" element={<AdminRoute><EventsPage /></AdminRoute>} />
          <Route path="/admin/validate" element={<AdminRoute><AdminValidatePage /></AdminRoute>} />
          <Route path="/admin/tournaments" element={<AdminRoute><TournamentsAdminPage /></AdminRoute>} />
          <Route path="/admin/fixtures" element={<AdminRoute><FixturesAdminPage /></AdminRoute>} />
          <Route path="/tickets" element={<AdminRoute><TicketsPage /></AdminRoute>} />
          <Route path="/pre-printed" element={<AdminRoute><PrePrintedPage /></AdminRoute>} />
          <Route path="/payments" element={<AdminRoute><PaymentsPage /></AdminRoute>} />
          <Route path="/reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
          <Route path="/staff" element={<AdminRoute><StaffManagementPage /></AdminRoute>} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
